/**
 * Slice 19e — trainer-written exemplar captions (text only, no source post).
 *
 * Rendered inside the template editor (both create and edit modes) so trainers
 * can hand-write 1–5 short examples that count toward sign-off alongside indexed
 * posts. The list is server-clamped to 5 entries; each entry is clamped to 1500
 * chars; empty/whitespace entries are dropped on save (see
 * `normalizeCustomExemplarTexts` in `adsTemplateExampleSync.ts`).
 */
import { useCallback } from "react";

const CUSTOM_EXEMPLAR_CAP = 5;
const CUSTOM_EXEMPLAR_TEXT_MAX = 1_500;

type Props = {
  texts: string[];
  setTexts: (texts: string[]) => void;
};

export function AdsLibraryTemplateCustomExemplarsField(props: Props) {
  const { texts, setTexts } = props;

  const updateOne = useCallback(
    (idx: number, value: string) => {
      setTexts(texts.map((t, i) => (i === idx ? value : t)));
    },
    [texts, setTexts],
  );

  const removeOne = useCallback(
    (idx: number) => setTexts(texts.filter((_, i) => i !== idx)),
    [texts, setTexts],
  );

  const addOne = useCallback(() => {
    if (texts.length >= CUSTOM_EXEMPLAR_CAP) return;
    setTexts([...texts, ""]);
  }, [texts, setTexts]);

  return (
    <section className="space-y-2 rounded-lg border border-sky-200/80 bg-sky-50/40 p-2 dark:border-sky-900/50 dark:bg-sky-950/15">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="text-[11px] font-semibold uppercase tracking-wide text-sky-900 dark:text-sky-200">
          Custom exemplars ({texts.length}/{CUSTOM_EXEMPLAR_CAP})
        </h5>
        <button
          type="button"
          onClick={addOne}
          disabled={texts.length >= CUSTOM_EXEMPLAR_CAP}
          className="rounded border border-sky-300 bg-white px-2 py-0.5 text-[11px] font-medium text-sky-900 hover:bg-sky-50 disabled:opacity-50 dark:border-sky-800 dark:bg-zinc-950 dark:text-sky-200 dark:hover:bg-sky-950/40"
        >
          Add custom exemplar
        </button>
      </div>

      {texts.length ? (
        <ul className="space-y-1.5">
          {texts.map((text, idx) => (
            <li key={idx} className="flex items-start gap-1">
              <textarea
                className="flex-1 rounded border border-sky-300 bg-white px-1.5 py-1 text-[11px] dark:border-sky-800 dark:bg-zinc-950 dark:text-sky-100"
                placeholder="Paste or type an example caption that illustrates this template…"
                value={text}
                maxLength={CUSTOM_EXEMPLAR_TEXT_MAX}
                rows={3}
                onChange={(e) => updateOne(idx, e.target.value)}
              />
              <button
                type="button"
                onClick={() => removeOne(idx)}
                aria-label={`Remove custom exemplar ${idx + 1}`}
                className="text-[16px] leading-none text-sky-700 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-100"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-sky-900/85 dark:text-sky-200/85">
          No custom exemplars yet. Add hand-written examples here to teach the
          template without an indexed post; each entry can be up to{" "}
          {CUSTOM_EXEMPLAR_TEXT_MAX} characters and counts toward sign-off.
        </p>
      )}
    </section>
  );
}
